import React, { useState, useRef, useEffect } from 'react';

/**
 * PeriodSelector — Shared period picker for Dashboard and Reports.
 * Presets: This Month, Last Month, This Quarter, Last Quarter, This Year, Last 6 Months.
 */

const PERIOD_PRESETS = [
  { key: 'this_month', label: 'This Month' },
  { key: 'last_month', label: 'Last Month' },
  { key: 'this_quarter', label: 'This Quarter' },
  { key: 'last_quarter', label: 'Last Quarter' },
  { key: 'this_year', label: 'This Year' },
  { key: 'last_6_months', label: 'Last 6 Months' },
];

export function getPeriodDates(periodKey) {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();
  const quarter = Math.floor(month / 3);

  switch (periodKey) {
    case 'this_month':
      return {
        start: new Date(year, month, 1),
        end: new Date(year, month + 1, 0),
        priorStart: new Date(year, month - 1, 1),
        priorEnd: new Date(year, month, 0),
      };
    case 'last_month':
      return {
        start: new Date(year, month - 1, 1),
        end: new Date(year, month, 0),
        priorStart: new Date(year, month - 2, 1),
        priorEnd: new Date(year, month - 1, 0),
      };
    case 'this_quarter':
      return {
        start: new Date(year, quarter * 3, 1),
        end: new Date(year, quarter * 3 + 3, 0),
        priorStart: new Date(year, (quarter - 1) * 3, 1),
        priorEnd: new Date(year, quarter * 3, 0),
      };
    case 'last_quarter':
      return {
        start: new Date(year, (quarter - 1) * 3, 1),
        end: new Date(year, quarter * 3, 0),
        priorStart: new Date(year, (quarter - 2) * 3, 1),
        priorEnd: new Date(year, (quarter - 1) * 3, 0),
      };
    case 'this_year':
      return {
        start: new Date(year, 0, 1),
        end: new Date(year, 11, 31),
        priorStart: new Date(year - 1, 0, 1),
        priorEnd: new Date(year - 1, 11, 31),
      };
    case 'last_6_months':
    default:
      return {
        start: new Date(year, month - 5, 1),
        end: new Date(year, month + 1, 0),
        priorStart: new Date(year, month - 11, 1),
        priorEnd: new Date(year, month - 5, 0),
      };
  }
}

export default function PeriodSelector({ value = 'last_6_months', onChange, compare = false, onCompareChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Format today's date as "WED, JUN 17, 2026"
  const today = new Date();
  const dateLabel = today.toLocaleDateString('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).toUpperCase().replace(/,/g, ',');

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          padding: '6px 12px',
          height: 'var(--d-control-h)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--btn-radius)',
          background: 'var(--color-surface)',
          fontSize: 'var(--d-font-ui)',
          fontWeight: 500,
          color: 'var(--color-text-default)',
          cursor: 'pointer',
        }}
      >
        <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
          <rect x="1" y="3" width="14" height="12" rx="2" stroke="currentColor" strokeWidth="1.5" fill="none" />
          <path d="M1 7h14" stroke="currentColor" strokeWidth="1.5" />
          <path d="M5 1v4M11 1v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
        </svg>
        {dateLabel}
        <svg width="10" height="10" viewBox="0 0 10 10" fill="currentColor">
          <path d="M2 4l3 3 3-3" stroke="currentColor" strokeWidth="1.5" fill="none" strokeLinecap="round" />
        </svg>
      </button>
      {open && (
        <div style={{
          position: 'absolute',
          top: '100%',
          right: 0,
          marginTop: 4,
          background: 'var(--color-surface)',
          border: '1px solid var(--neutral-200)',
          borderRadius: 'var(--card-radius)',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          zIndex: 50,
          minWidth: 180,
          padding: '4px 0',
        }}>
          {PERIOD_PRESETS.map(preset => (
            <button
              key={preset.key}
              onClick={() => { onChange(preset.key); setOpen(false); }}
              style={{
                display: 'block',
                width: '100%',
                padding: '8px 14px',
                border: 'none',
                background: preset.key === value ? 'var(--neutral-100)' : 'transparent',
                textAlign: 'left',
                fontSize: 13,
                color: 'var(--color-text-default)',
                cursor: 'pointer',
                fontWeight: preset.key === value ? 600 : 400,
              }}
            >
              {preset.label}
            </button>
          ))}
          {/* Compare to prior period toggle */}
          {onCompareChange && (
            <div style={{ borderTop: '1px solid var(--neutral-200)', padding: '8px 14px', marginTop: 4 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: 'var(--color-text-default)', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={compare}
                  onChange={e => onCompareChange(e.target.checked)}
                  style={{ accentColor: 'var(--color-action-primary)' }}
                />
                Compare to prior period
              </label>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export { PERIOD_PRESETS };
